import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Tag, Receipt, Users, MessageSquare, Database, TrendingUp, BarChart3, Zap } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, Cell,
} from "recharts";
import api from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";

const CARDS = [
  { key: "products", label: "Produk", icon: Package, color: "#2563EB" },
  { key: "promos", label: "Promo Aktif", icon: Tag, color: "#D97706" },
  { key: "orders", label: "Pesanan", icon: Receipt, color: "#059669" },
  { key: "leads", label: "Leads", icon: Users, color: "#DB2777" },
  { key: "conversations", label: "Percakapan", icon: MessageSquare, color: "#7C3AED" },
];

const BAR_COLORS = ["#2563EB", "#059669", "#D97706", "#DB2777", "#7C3AED", "#0891B2"];

export default function Overview({ business }) {
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState({ daily_leads: [], top_products: [] });
  useEffect(() => {
    api.get(`/businesses/${business.id}/stats`).then((r) => setStats(r.data));
    api.get(`/businesses/${business.id}/analytics`).then((r) => setAnalytics(r.data));
  }, [business.id]);

  const db = business.db_config;
  const accent = business.config.accent_color;
  const hasHits = analytics.top_products.some((p) => p.hits > 0);
  const sv = analytics.savings;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {CARDS.map((c, i) => (
          <motion.div key={c.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="rounded-2xl border border-border bg-card p-5" data-testid={`stat-${c.key}`}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ background: c.color + "1a" }}>
              <c.icon className="w-4 h-4" style={{ color: c.color }} />
            </div>
            <p className="font-display text-3xl font-bold tracking-tight">{stats[c.key] ?? "—"}</p>
            <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="savings-card">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-emerald-600" />
          <h3 className="font-display font-semibold">Penghematan Token AI</h3>
        </div>
        {sv && sv.total > 0 ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="font-display text-3xl font-bold text-emerald-600" data-testid="savings-faq-pct">{sv.faq_pct}%</p>
                <p className="text-xs text-muted-foreground mt-1">Chat dijawab FAQ/template (tanpa AI)</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold" data-testid="savings-tokens">{sv.tokens_saved.toLocaleString("id-ID")}</p>
                <p className="text-xs text-muted-foreground mt-1">Estimasi token dihemat</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-emerald-600" data-testid="savings-idr">Rp{(sv.idr_saved || 0).toLocaleString("id-ID")}</p>
                <p className="text-xs text-muted-foreground mt-1">Estimasi hemat biaya (tarif Gemini Flash)</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold" data-testid="savings-total">{sv.total}</p>
                <p className="text-xs text-muted-foreground mt-1">Total balasan ({sv.faq} FAQ · {sv.ai} AI)</p>
              </div>
            </div>
            <div className="h-2.5 rounded-full bg-muted overflow-hidden flex" data-testid="savings-bar">
              <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${sv.faq_pct}%` }} />
              <div className="bg-accent transition-all duration-500" style={{ width: `${100 - sv.faq_pct}%` }} />
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
              <span>FAQ & template: {sv.faq}</span>
              <span>AI (Gemini Flash): {sv.ai}</span>
            </div>
            {sv.daily?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border" data-testid="chart-daily-savings">
                <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-3">Tren Penghematan Harian (7 hari)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={sv.daily} margin={{ left: -20, right: 8 }}>
                    <defs>
                      <linearGradient id="saveGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }}
                      formatter={(value, name) => name === "tokens"
                        ? [`${Number(value).toLocaleString("id-ID")} token`, "Token dihemat"]
                        : [`Rp${Number(value).toLocaleString("id-ID")}`, "Hemat biaya"]} />
                    <Area type="monotone" dataKey="tokens" stroke="#059669" strokeWidth={2.5} fill="url(#saveGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="savings-empty">
            Belum ada data. Statistik penghematan terisi otomatis setelah ada percakapan —
            balasan yang dijawab FAQ/template dihitung sebagai token yang dihemat.
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="chart-daily-leads">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-4 h-4 text-accent" />
            <h3 className="font-display font-semibold">Lead Harian (7 hari)</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={analytics.daily_leads} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="leadGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
              <Area type="monotone" dataKey="leads" stroke={accent} strokeWidth={2.5} fill="url(#leadGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6" data-testid="chart-top-products">
          <div className="flex items-center gap-2 mb-5">
            <BarChart3 className="w-4 h-4 text-accent" />
            <h3 className="font-display font-semibold">Produk Paling Ditanyakan</h3>
          </div>
          {hasHits ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={analytics.top_products} layout="vertical" margin={{ left: 20, right: 12 }}>
                <XAxis type="number" allowDecimals={false} hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                <Bar dataKey="hits" radius={[0, 6, 6, 0]} barSize={16}>
                  {analytics.top_products.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-center text-sm text-muted-foreground px-6">
              Belum ada data. Ajukan pertanyaan produk di Widget Playground untuk mengisi grafik ini.
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-4 h-4" />
          <h3 className="font-display font-semibold">Sumber Data Bisnis</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Engine</p>
            <p className="font-mono uppercase">{db.type}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Host</p>
            <p className="font-mono truncate">{db.host || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Database</p>
            <p className="font-mono truncate">{db.db_name || "—"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mb-1">Status</p>
            <Badge variant="outline" className={db.status === "connected"
              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
              : "bg-muted text-muted-foreground"}>
              {db.status === "connected" ? "Terhubung" : db.status === "failed" ? "Gagal" : "Belum diatur"}
            </Badge>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          Bila koneksi gagal atau data kosong, chatbot otomatis memakai data internal. Promo selalu berasal dari data internal.
        </p>
      </div>
    </div>
  );
}
